import { useCallback, useContext, useMemo, useState } from 'react'
import LinkWithStatus from '../common/LinkWithStatus'
import { NavContext } from '../../context/global_context'
import { getRequest, postRequest } from '../../services/base'
import LoadingSpinnerOverlay from '../common/LoadingSpinnerOverlay'
import { useParams } from 'react-router-dom'
import InputText from '../common/InputText'
import Button from '../common/Button'
import { formatEmptyValue } from '../../utils/common'
import { Empty, Flex, Modal, Space, Table, Typography } from 'antd'
import EllipsisTooltip from '../common/EllipsisTooltip'
import {useIntl} from 'react-intl'
import RecordsCount from './RecordsCount'

const GenerateWiresButton = ({
  is_inspection_points_wire = false,
  is_power_distribution_wire = false,
  onSuccess,
}) => {
  const intl = useIntl()
  const { vehicle } = useParams()
  const [navState, navDispatch] = useContext(NavContext)

  const [generateWireButtonStatus, setGenerateWireButtonStatus] =
    useState(false)
  const [showHarnesses, setShowHarnesses] = useState(false)
  const [harnesses, setHarnesses] = useState({})
  const [filteredHarnesses, setFilteredHarnesses] = useState([])
  const [selectedHarnesses, setSelectedHarnesses] = useState([])
  
  const getHarnessList = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'generateWires.fetchingHarnesses' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/harnesses/harness_names/list/?vehicle=${vehicle}&is_inspection_points_wire=${is_inspection_points_wire}&is_power_distribution_wire=${is_power_distribution_wire}`
    )
    if (res?.status) {
      setHarnesses(res.data)
      setFilteredHarnesses(Object.keys(res.data))
    } else {
      setHarnesses({})
      setFilteredHarnesses([])
    }

    navDispatch({
      type: 'loader',
      data: {
        caption: '',
        showLoader: false,
      },
      notification: {},
    })
  }

  const onGenerateWiresClick = async (connectors = []) => {
    setShowHarnesses(true)
    getHarnessList()
  }

  const generateWires = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'generateWires.processing' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await postRequest(`/api/wire/generate_wires/`, {
      vehicle: vehicle,
      connectors: [],
      clean_wires: false,
      is_inspection_points_wire: is_inspection_points_wire,
      is_power_distribution_wire: is_power_distribution_wire,
      harnesses: selectedHarnesses,
    })
    navDispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'generateWires.success' }),
      },
    })
    resetGenerateWiresButtonState()
    await getGenerateWireButtonStatus()
  }

  const getGenerateWireButtonStatus = useCallback(async () => {
    let response = await getRequest(
      `/api/dtcs/generate_wire_button_status/?vehicle=${vehicle}`
    )
    setGenerateWireButtonStatus(response.show_loader || false)
    if (response.show_loader) {
      setTimeout(() => {
        getGenerateWireButtonStatus()
      }, 7000)
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'generateWires.completed' }),
        },
      })
      onSuccess?.()
    }
  }, [vehicle, onSuccess, intl, navDispatch])

  const resetGenerateWiresButtonState = () => {
    setShowHarnesses(false)
    setHarnesses({})
    setFilteredHarnesses([])
  }

  const dataSource = useMemo(() => {
    if (!filteredHarnesses?.length) return []

    return filteredHarnesses?.map((harness, harnessIndex) => {
      return {
        harness: harness,
        harnesses: formatEmptyValue(harnesses[harness].join(', ')),
        rowKey: `harness-${harnessIndex}-${harness}`,
      }
    })
  }, [filteredHarnesses])

  const columns = [
    {
      title: '',
      width: 400,
      render: (_, record) => {
        return (
          <>
            <EllipsisTooltip className="bolder ellipsis">
              {record.harness}
            </EllipsisTooltip>
            <EllipsisTooltip className="ellipsis">
              {record.harnesses}
            </EllipsisTooltip>
          </>
        )
      },
    },
  ]
  /* Handle Table Row selection */
  const selectedIds = useMemo(() => {
    return selectedHarnesses.map((item) => item)
  }, [selectedHarnesses])

  const rowSelection = {
    hideSelectAll: true,
    selectedRowKeys: [...selectedIds],
    onChange: (newSelectedRowKeys) => {
      setSelectedHarnesses(newSelectedRowKeys)
    },
  }

  return (
    <>
      <Modal
        open={showHarnesses}
        title={intl.formatMessage({ id: 'generateWires.title' })}
        width="800px"
        className="generate-wires-modal"
        centered={true}
        footer={null}
        closable={true}
        onCancel={resetGenerateWiresButtonState}
      >
        <Flex vertical gap={16} style={{ minWidth: 0 }}>
          <InputText
            name="search_value"
            placeholder={intl.formatMessage({ id: 'generateWires.searchPlaceholder' })}
            type="search"
            isRequired={false}
            onChange={(value) => {
              setFilteredHarnesses(
                Object.keys(harnesses).filter((harness) =>
                  harness
                    .toLocaleLowerCase()
                    .includes(value.toLocaleLowerCase())
                )
              )
            }}
          />

          <Table
            rowKey="harness"
            columns={columns}
            dataSource={dataSource}
            tableLayout="fixed"
            scroll={{ y: '60vh' }}
            showHeader={false}
            pagination={false}
            rowSelection={rowSelection}
            onRow={(record) => ({
              onClick: (e) => {
                // Prevent toggle if the actual checkbox is clicked.
                if (e.target.closest('.ant-checkbox-wrapper')) {
                  return
                }

                setSelectedHarnesses((prev) =>
                  prev.includes(record.harness)
                    ? prev.filter((key) => key !== record.harness)
                    : [...prev, record.harness]
                )
              },
            })}
            locale={{
              emptyText: (
                <div style={{ padding: '40px' }}>
                  <Empty description={ <RecordsCount count={0} /> }  />
                </div>
              ),
            }}
          />

          <Button
            type="primary"
            className={`generate-wires-button ${selectedHarnesses?.length < 1 && 'disabled'}`}
            onClick={() => {
              generateWires()
            }}
            key={`generate-wire-btn`}
          >
            {intl.formatMessage({ id: 'generateWires.generateWires' })}
          </Button>
        </Flex>
      </Modal>

      <LoadingSpinnerOverlay active={generateWireButtonStatus} />

      <LinkWithStatus
        href={`/vehicles/generate_wires`}
        onClickFunc={(e) => {
          onGenerateWiresClick([])
        }}
        key={`generate-all-wire-btn`}
      >
        {intl.formatMessage({ id: 'generateWires.generateWires' })}
      </LinkWithStatus>
    </>
  )
}

export default GenerateWiresButton
